export type CalendarSyncSummary = {
  last_sync_status?: string | null;
} | null | undefined;

export type GuidedClassScheduleSyncState =
  | 'not_connected'
  | 'success_with_events'
  | 'success_empty'
  | 'failed'
  | 'syncing';

export function classEventSeries<T extends { source_event_id: string; status: string; all_day: boolean }>(
  preview: T[],
): T[] {
  const seen = new Set<string>();
  return preview.filter((event) => {
    if (event.status === 'cancelled' || event.all_day || seen.has(event.source_event_id)) return false;
    seen.add(event.source_event_id);
    return true;
  });
}

export function selectedClassEvents<T extends { source_event_id: string; status: string; all_day: boolean }>(
  preview: T[], mode: 'all' | 'selected', selected: string[],
): T[] {
  const ids = new Set(selected);
  return preview.filter((event) => event.status !== 'cancelled' && !event.all_day
    && (mode === 'all' || ids.has(event.source_event_id)));
}

export function calendarLinkValidationError(value: string): string | null {
  const link = value.trim();
  if (!link) return null;

  let parsed: URL;
  try {
    parsed = new URL(link);
  } catch {
    return 'Enter the full calendar subscription link, including its address.';
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'webcal:') {
    return 'Use a subscription link that starts with https:// or webcal://.';
  }
  if (!parsed.hostname) {
    return 'Enter a complete calendar subscription link.';
  }
  const looksLikeSubscription = /(?:\.ics(?:$|[/?#])|webcal|ical|calendar|feed)/i
    .test(`${parsed.pathname}${parsed.search}`);
  if (!looksLikeSubscription) {
    return 'This looks like a regular web page. Paste the calendar subscription link (WebCal or HTTPS), not the browser address bar URL.';
  }
  return null;
}

export function calendarConnectionLabel(connection: CalendarSyncSummary): string {
  if (!connection) return 'Not connected';
  if (connection.last_sync_status === 'success') return 'Connected';
  if (connection.last_sync_status === 'failed') return 'Connection issue';
  return 'Syncing';
}

export function calendarEmptyPreviewMessage(connection: CalendarSyncSummary): string {
  if (!connection) return 'Connect a school calendar to preview upcoming classes.';
  if (connection.last_sync_status === 'success') {
    return 'Calendar connected and up to date, but no upcoming classes were found in the synced window.';
  }
  if (connection.last_sync_status === 'failed') {
    return 'No saved classes are available to show. Check the calendar connection and try again.';
  }
  return 'Your calendar is connecting. Upcoming classes will appear after the first sync.';
}

export function guidedClassScheduleSyncMessage(
  syncState: GuidedClassScheduleSyncState,
  eventCount: number,
): string {
  if (syncState === 'success_with_events') {
    return `Connected and synced. ${eventCount} upcoming timed ${eventCount === 1 ? 'class' : 'classes'} found.`;
  }
  if (syncState === 'success_empty') {
    return 'Connected and synced, but no upcoming timed classes were found in the current sync window.';
  }
  if (syncState === 'failed') {
    return 'Connection saved, but schedule sync failed. Check the feed link and retry.';
  }
  if (syncState === 'syncing') {
    return 'Connection is in progress. Class previews will appear after sync completes.';
  }
  return 'Not connected yet.';
}

export function guidedClassScheduleErrorMessage(errorCode: string | null | undefined): string | null {
  if (!errorCode) return null;
  if (errorCode === 'invalid_calendar_link') return 'The feed link is invalid. Paste the full WebCal or HTTPS subscription link.';
  if (errorCode === 'calendar_sync_failed') return 'The link was saved but could not be synchronized. Confirm the link is active, then retry.';
  return 'Guided setup could not complete. Retry or continue with manual setup.';
}
